import { useState } from "react";
import SEO from "@/components/SEO";
import { fetchLeads } from "@/lib/api";
import { useCatalog } from "@/lib/catalog";
import { CATEGORIES, SENSORS, APPLICATIONS, POWER } from "@/data/products";
import { Lock, RefreshCw, Upload, RotateCcw, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const specsToText = (specs) => Object.entries(specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
const textToSpecs = (text) => {
  const out = {};
  text.split("\n").forEach((line) => {
    const i = line.indexOf(":");
    if (i > 0) out[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  });
  return out;
};

function BulkUpload({ adminKey, onDone }) {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const send = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setResults(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/products/images/bulk`, { method: "POST", headers: { "X-Admin-Key": adminKey }, body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setResults(data.results);
      const okCount = data.results.filter((r) => r.ok).length;
      toast.success(`${okCount} of ${data.results.length} images uploaded and live`);
      onDone();
    } catch {
      toast.error("Bulk upload failed — check your admin key");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="bulk-upload">
      <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <h3 className="font-display text-lg font-bold text-ink">Bulk photo swap</h3>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed max-w-lg">
            Select all your product photos at once. Each filename must contain the product's order number — e.g. <span className="font-mono text-signal">06901.png</span>, <span className="font-mono text-signal">06934-side-view.jpg</span>. They map to products automatically and go live instantly.
          </p>
        </div>
        <label className="shrink-0 flex items-center justify-center gap-2 px-6 py-3 bg-signal text-white text-sm font-medium cursor-pointer hover:bg-[#5c8f23] transition-colors" data-testid="bulk-upload-label">
          <Upload size={15} /> {busy ? "Uploading…" : "Choose photos"}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="hidden" onChange={send} disabled={busy} data-testid="bulk-upload-input" />
        </label>
      </div>
      {results && (
        <ul className="mt-4 border-t border-zinc-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5" data-testid="bulk-upload-results">
          {results.map((r, i) => (
            <li key={i} className={`text-xs font-mono flex justify-between gap-3 ${r.ok ? "text-zinc-600" : "text-red-600"}`}>
              <span className="truncate">{r.file}</span>
              <span className="shrink-0">{r.ok ? `→ ${r.product} ✓` : r.error}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProductEditor({ product, adminKey, onBack, onSaved }) {
  const [form, setForm] = useState({
    name: product.name,
    tagline: product.tagline,
    category: product.category,
    sensor: product.sensor || "",
    power: product.power,
    applications: [...product.applications],
    capabilities: (product.capabilities || []).join(", "),
    overview: (product.overview || []).join("\n"),
    features: (product.features || []).join("\n"),
    specs: specsToText(product.specs),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const headers = { "Content-Type": "application/json", "X-Admin-Key": adminKey };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        category: form.category,
        sensor: form.sensor || null,
        power: form.power,
        applications: form.applications,
        capabilities: form.capabilities.split(",").map((s) => s.trim()).filter(Boolean),
        overview: form.overview.split("\n").map((s) => s.trim()).filter(Boolean),
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
        specs: textToSpecs(form.specs),
      };
      const res = await fetch(`${API}/product-overrides/${product.id}`, { method: "PUT", headers, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      toast.success(`Saved ${product.orderNumber}`);
      onSaved();
    } catch {
      toast.error("Save failed — check your admin key");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/products/${product.id}/image`, { method: "POST", headers: { "X-Admin-Key": adminKey }, body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      toast.success("Image uploaded — now live on the site");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const resetOverrides = async () => {
    if (!window.confirm("Remove all custom edits for this product and restore the original spec-sheet data?")) return;
    await fetch(`${API}/product-overrides/${product.id}`, { method: "DELETE", headers: { "X-Admin-Key": adminKey } });
    toast.success("Restored original data");
    onSaved();
    onBack();
  };

  const toggleApp = (a) =>
    setForm((f) => ({ ...f, applications: f.applications.includes(a) ? f.applications.filter((x) => x !== a) : [...f.applications, a] }));

  const inputCls = "w-full border border-zinc-200 bg-bone px-3 py-2.5 text-sm text-ink outline-none focus:border-signal transition-colors";

  return (
    <div className="bg-white border border-zinc-200 p-6 md:p-8" data-testid="product-editor">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <button onClick={onBack} className="text-sm text-zinc-500 hover:text-signal flex items-center gap-1.5" data-testid="editor-back-btn">
          <ArrowLeft size={14} /> All products
        </button>
        <div className="flex gap-3">
          <button onClick={resetOverrides} className="text-xs px-4 py-2 border border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-600 flex items-center gap-1.5" data-testid="editor-reset-btn">
            <RotateCcw size={12} /> Reset to original
          </button>
          <button onClick={save} disabled={saving} className="text-xs px-5 py-2 bg-signal text-white font-medium hover:bg-[#5c8f23] flex items-center gap-1.5 disabled:opacity-60" data-testid="editor-save-btn">
            <Save size={12} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <div>
          <div className="spotlight border border-zinc-200 h-56 flex items-center justify-center overflow-hidden">
            <img src={product.image} alt={product.name} className="max-h-44 w-auto object-contain" data-testid="editor-image-preview" />
          </div>
          <label className="mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-forest text-white text-sm font-medium cursor-pointer hover:bg-signal transition-colors" data-testid="image-upload-label">
            <Upload size={15} /> {uploading ? "Uploading…" : "Upload new image"}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="image-upload-input" />
          </label>
          <p className="text-[11px] text-zinc-400 mt-2 leading-relaxed">PNG, JPEG or WebP up to 8MB. Use a high-resolution photo on a clean background for the sharpest result.</p>
        </div>

        <div className="space-y-5">
          <p className="font-mono text-xs text-signal">Order № {product.orderNumber}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Product name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="edit-name" />
            </label>
            <label className="block sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Tagline</span>
              <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={inputCls} data-testid="edit-tagline" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Category</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="edit-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Sensor</span>
              <select value={form.sensor} onChange={(e) => setForm({ ...form, sensor: e.target.value })} className={inputCls} data-testid="edit-sensor">
                <option value="">None</option>
                {SENSORS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Power</span>
              <select value={form.power} onChange={(e) => setForm({ ...form, power: e.target.value })} className={inputCls} data-testid="edit-power">
                {POWER.map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Applications</span>
              <div className="flex flex-wrap gap-2">
                {APPLICATIONS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleApp(a)}
                    aria-pressed={form.applications.includes(a)}
                    data-testid={`edit-app-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`text-xs px-3 py-1.5 border ${form.applications.includes(a) ? "bg-signal text-white border-signal" : "border-zinc-200 text-zinc-600"}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <label className="block sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Capabilities (comma separated)</span>
              <input value={form.capabilities} onChange={(e) => setForm({ ...form, capabilities: e.target.value })} className={inputCls} data-testid="edit-capabilities" />
            </label>
            <label className="block sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Overview bullets (one per line)</span>
              <textarea rows={5} value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} className={`${inputCls} resize-y`} data-testid="edit-overview" />
            </label>
            <label className="block sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Features (one per line)</span>
              <textarea rows={8} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className={`${inputCls} resize-y`} data-testid="edit-features" />
            </label>
            <label className="block sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Technical specs (one per line, “Label: Value”)</span>
              <textarea rows={8} value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} className={`${inputCls} resize-y font-mono text-xs`} data-testid="edit-specs" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("amaya_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const { products, refresh } = useCatalog();

  const auth = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      await fetchLeads(key);
      sessionStorage.setItem("amaya_admin_key", key);
      setAuthed(true);
    } catch {
      setError("Invalid admin key or server error.");
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      setLeads(await fetchLeads(key));
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const enterProducts = () => setTab("products");

  return (
    <div className="bg-bone min-h-screen pt-32 pb-24" data-testid="admin-page">
      <SEO title="Admin" path="/admin" />
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-ink">Site manager</h1>
        <p className="text-zinc-500 text-sm mt-2">Leads inbox and product catalog manager. Changes go live instantly.</p>

        {!authed ? (
          <form onSubmit={auth} className="mt-10 bg-white border border-zinc-200 p-8 max-w-md" data-testid="admin-login-form">
            <label className="block">
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Admin Key</span>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className="flex-1 border border-zinc-200 bg-bone px-4 py-3 text-sm outline-none focus:border-signal"
                  data-testid="admin-key-input"
                />
                <button type="submit" className="px-5 py-3 bg-forest text-white text-sm font-medium flex items-center gap-2" data-testid="admin-login-btn">
                  <Lock size={14} /> {loading ? "…" : "Enter"}
                </button>
              </div>
            </label>
            {error && <p className="text-sm text-red-600 mt-3" data-testid="admin-error">{error}</p>}
          </form>
        ) : (
          <>
            <div className="flex gap-2 mt-10 mb-6" role="tablist">
              {[["leads", "Leads"], ["products", "Products"]].map(([v, label]) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={tab === v}
                  onClick={() => { setTab(v); setEditing(null); if (v === "leads" && !leads) loadLeads(); }}
                  data-testid={`admin-tab-${v}`}
                  className={`px-5 py-2.5 text-sm font-medium border transition-colors ${tab === v ? "bg-signal text-white border-signal" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                >
                  {label}
                </button>
              ))}
              {tab === "leads" && (
                <button onClick={loadLeads} className="ml-auto text-xs font-medium text-signal flex items-center gap-1" data-testid="admin-refresh-btn">
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
              )}
            </div>

            {tab === "leads" && (
              leads === null ? (
                <div className="bg-white border border-zinc-200 p-12 text-center">
                  <button onClick={loadLeads} className="px-6 py-3 bg-forest text-white text-sm font-medium" data-testid="admin-load-leads-btn">Load leads</button>
                </div>
              ) : (
                <div className="bg-white border border-zinc-200 overflow-x-auto spec-scroll" data-testid="admin-leads-table">
                  <table className="w-full text-sm min-w-[800px]">
                    <thead>
                      <tr className="border-b border-zinc-200 text-left">
                        {["Date", "Type", "Name", "Company", "Email", "Phone", "Project", "Message"].map((h) => (
                          <th key={h} className="px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} className="border-b border-zinc-100 hover:bg-bone">
                          <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">{new Date(l.createdAt).toLocaleString()}</td>
                          <td className="px-4 py-3"><span className="text-[11px] px-2 py-0.5 bg-forest text-white uppercase font-mono">{l.type}</span></td>
                          <td className="px-4 py-3 text-ink whitespace-nowrap">{l.firstName} {l.lastName}</td>
                          <td className="px-4 py-3 text-zinc-600">{l.company || "—"}</td>
                          <td className="px-4 py-3 text-zinc-600">{l.email}</td>
                          <td className="px-4 py-3 text-zinc-600">{l.phone || "—"}</td>
                          <td className="px-4 py-3 text-zinc-600 text-xs">{[l.projectType, l.projectSize, l.location].filter(Boolean).join(" · ") || "—"}</td>
                          <td className="px-4 py-3 text-zinc-600 text-xs max-w-[240px] truncate">{l.message || "—"}</td>
                        </tr>
                      ))}
                      {leads.length === 0 && (
                        <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-400 text-sm">No leads yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {tab === "products" && (
              editing ? (
                <ProductEditor
                  product={products.find((p) => p.id === editing)}
                  adminKey={key}
                  onBack={() => setEditing(null)}
                  onSaved={refresh}
                />
              ) : (
                <>
                <BulkUpload adminKey={key} onDone={refresh} />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-products-grid">
                  {products.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setEditing(p.id)}
                      data-testid={`admin-edit-${p.id}`}
                      className="bg-white border border-zinc-200 hover:border-signal transition-colors text-left p-4 group"
                    >
                      <div className="h-24 flex items-center justify-center mb-3 bg-bone">
                        <img src={p.image} alt="" loading="lazy" className="max-h-20 w-auto object-contain" />
                      </div>
                      <p className="font-mono text-[10px] text-signal">№ {p.orderNumber}</p>
                      <p className="text-xs text-ink mt-1 leading-snug line-clamp-2 group-hover:text-signal transition-colors">{p.name}</p>
                    </button>
                  ))}
                </div>
                </>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
